import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import {
  Treatment,
  treatmentsService,
} from '../../../../services/treatments';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
import { redactError } from '../../../../utils/redactError';
const logger = createLogger('TreatmentsData');

type TreatmentsDataScreenRouteProp = RouteProp<RootStackParamList, 'TreatmentsData'>;

type RouteParams = {
  treatmentId?: string;
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const TreatmentsDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const [treatmentInfo, setTreatmentInfo] = useState<Treatment | null>(null);
  const [loading, setLoading] = useState(true);

  const treatmentId = route.params?.treatmentId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;

  useEffect(() => {
    loadTreatmentInfo();
  }, [targetUserId]);

  const loadTreatmentInfo = async () => {
    try {
      logger.debug('TreatmentsDataScreen: Starting to load treatment info');
      logger.debug('TreatmentsDataScreen: treatmentId:', treatmentId);
      logger.debug('TreatmentsDataScreen: targetUserId:', targetUserId);

      if (treatmentId) {
        logger.debug('TreatmentsDataScreen: Loading specific treatment by ID:', treatmentId);
        const info = await treatmentsService.getTreatmentById(treatmentId);
        logger.debug('TreatmentsDataScreen: Loaded treatment by ID:', info);
        setTreatmentInfo(info);
      } else {
        // Load all treatments, optionally for another user (successor view)
        logger.debug('TreatmentsDataScreen: Loading all treatments');
        const allInfo = await treatmentsService.getAllTreatments(targetUserId);
        logger.debug('TreatmentsDataScreen: Loaded treatments:', allInfo);
        logger.debug('TreatmentsDataScreen: Number of treatments found:', allInfo.length);

        if (allInfo.length > 0) {
          logger.debug('TreatmentsDataScreen: Setting first treatment:', allInfo[0]);
          setTreatmentInfo(allInfo[0]);
        } else {
          logger.debug('TreatmentsDataScreen: No treatments found');
        }
      }
    } catch (error) {
      logger.error('TreatmentsDataScreen: Error loading treatment info:', redactError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (treatmentInfo) {
      // Map the data from treatmentInfo to the format expected by TreatmentsInputScreen
      const mappedData = {
        id: treatmentInfo.id,
        treatmentType: treatmentInfo.treatmentType,
        purpose: treatmentInfo.purpose,
        facilityName: treatmentInfo.facilityName,
        location: treatmentInfo.location,
        phone: treatmentInfo.phone,
        insurance: treatmentInfo.insurance,
        cost: treatmentInfo.cost,
        notes: treatmentInfo.notes,
      };

      navigation.navigate('TreatmentsInput', {
        treatmentData: mappedData,
        isEditing: true
      });
    } else {
      navigation.navigate('TreatmentsInput');
    }
  };

  const handleDelete = () => {
    if (treatmentInfo) {
      Alert.alert(
        'Delete Treatment',
        'Are you sure you want to delete this treatment? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await treatmentsService.deleteTreatment(treatmentInfo.id);

                // CRITICAL: Check if this was the last entry and reset task if so
                const remainingData = await treatmentsService.getTreatments(13);
                if (!remainingData || remainingData.length === 0) {
                  await tasksService.resetTask('current_therapy');
                }

                navigation.navigate('MedicalChecklist');
              } catch (error) {
                logger.error('Error deleting treatment info:', error);
                Alert.alert('Error', 'Failed to delete treatment');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    navigation.navigate('MedicalChecklist');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Treatment Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Treatment Details</Text>

      {viewOnly && (
        <View style={styles.viewOnlyBanner}>
          <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
        </View>
      )}

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {treatmentInfo ? (
            <>
              {treatmentInfo.treatmentType && (
                <InfoItem label="Type of Treatment or Therapy" value={treatmentInfo.treatmentType} />
              )}
              {treatmentInfo.purpose && (
                <InfoItem label="Reason for Treatment" value={treatmentInfo.purpose} />
              )}
              {treatmentInfo.facilityName && (
                <InfoItem label="Treatment Facility Name" value={treatmentInfo.facilityName} />
              )}
              {treatmentInfo.location && (
                <InfoItem label="Location of Treatment" value={treatmentInfo.location} />
              )}
              {treatmentInfo.phone && (
                <InfoItem label="Facility Phone Number" value={treatmentInfo.phone} />
              )}
              {treatmentInfo.insurance && (
                <InfoItem label="Covered by Insurance?" value={treatmentInfo.insurance} />
              )}
              {treatmentInfo.cost && (
                <InfoItem label="Cost" value={treatmentInfo.cost} />
              )}
              {treatmentInfo.notes && (
                <InfoItem label="Notes" value={treatmentInfo.notes} />
              )}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton]}
                      onPress={handleDelete}
                    >
                      <Text style={styles.buttonText}>Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={handleEdit}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>
                {viewOnly ? 'This user has not added treatment information yet' : 'No treatment found.'}
              </Text>
              {!viewOnly && (
                <TouchableOpacity
                  style={[styles.button, styles.editButton, { marginTop: 20 }]}
                  onPress={handleEdit}
                >
                  <Text style={styles.buttonText}>Add Treatment</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  deleteButton: {
    backgroundColor: '#FF4444',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});
